import { createApi } from "@reduxjs/toolkit/query/react";

import { createServiceBaseQuery } from "../../../../../store/client/serviceBaseQuery";

import type { AccountFile } from "../../../types/AccountFile";

// Discriminated union — TS enforces the parent id at the call site so it's
// impossible to ask for "conversation" files without saying which conversation.
export type FilesQueryArg =
  | { scope: "account" }
  | { scope: "conversation"; conversationId: string }
  | { scope: "group"; groupId: string };
import {
  parseSheetPreview,
  parseSpreadsheet,
  type SheetData,
  type SheetPreview,
} from "./parsers/spreadsheet";
import { parseTextPreview, type TextPreview } from "./parsers/text";

export type { SheetPreview } from "./parsers/spreadsheet";
export type { TextPreview } from "./parsers/text";

// First 5 KB is enough to recover the header + ~5 data rows for typical CSVs/TSVs.
const SPREADSHEET_PREVIEW_RANGE = "bytes=0-4999";
// First 1.5 KB is plenty for an ~800-char snippet plus UTF-8 overhead.
const TEXT_PREVIEW_RANGE = "bytes=0-1499";

export const accountFilesService = createApi({
  reducerPath: "accountFilesService",
  baseQuery: createServiceBaseQuery("zeusUri"),
  tagTypes: ["Files"],
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getFiles: builder.query<AccountFile[], FilesQueryArg>({
      query: (arg) => {
        const params: Record<string, string> = { scope: arg.scope };
        if (arg.scope === "conversation") params["conversationId"] = arg.conversationId;
        if (arg.scope === "group") params["groupId"] = arg.groupId;
        return { url: "/api/files", params };
      },
      providesTags: (_result, _err, arg) => {
        switch (arg.scope) {
          case "account":
            return [{ type: "Files", id: "ACCOUNT" }];
          case "conversation":
            return [{ type: "Files", id: `CONV-${arg.conversationId}` }];
          case "group":
            return [{ type: "Files", id: `GROUP-${arg.groupId}` }];
        }
      },
    }),

    uploadAccountFile: builder.mutation<AccountFile, FormData>({
      query: (formData) => ({
        url: "/api/files",
        method: "POST",
        body: formData,
      }),
      // FormData scope isn't visible to the mutation builder; invalidate the tag
      // type broadly so every cached file list refreshes regardless of scope.
      invalidatesTags: ["Files"],
    }),

    deleteAccountFile: builder.mutation<void, { fileId: string }>({
      query: ({ fileId }) => ({
        url: `/api/files/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Files"],
    }),

    getFileRetrieveUrl: builder.query<{ url: string }, { fileId: string }>({
      query: ({ fileId }) => `/api/files/${fileId}/retrieve`,
      keepUnusedDataFor: 600,
    }),

    getSpreadsheetData: builder.query<SheetData[], { fileId: string; filename?: string }>(
      {
        queryFn: async ({ fileId, filename }, queryApi) => {
          // Reuse the getFileRetrieveUrl endpoint so both FileViewerPanel and getSpreadsheetData
          // share the same RTK Query cache entry, avoiding a duplicate /retrieve request.
          const urlResult = await queryApi.dispatch(
            accountFilesService.endpoints.getFileRetrieveUrl.initiate({
              fileId,
            }),
          );
          if (urlResult.error) {
            return { error: urlResult.error };
          }

          try {
            const response = await fetch(urlResult.data.url);
            if (!response.ok) {
              return {
                error: {
                  status: response.status,
                  data: "Failed to download file",
                },
              };
            }

            const arrayBuffer = await response.arrayBuffer();
            return { data: parseSpreadsheet(arrayBuffer, filename) };
          } catch (e) {
            return {
              error: {
                status: "FETCH_ERROR" as const,
                error: e instanceof Error ? e.message : "Failed to load file",
              },
            };
          }
        },
        keepUnusedDataFor: 300,
      },
    ),

    getSpreadsheetPreview: builder.query<
      SheetPreview,
      { fileId: string; delimiter?: string }
    >({
      queryFn: async ({ fileId, delimiter = "," }, queryApi) => {
        const urlResult = await queryApi.dispatch(
          accountFilesService.endpoints.getFileRetrieveUrl.initiate({ fileId }),
        );
        if (urlResult.error) return { error: urlResult.error };

        try {
          const response = await fetch(urlResult.data.url, {
            headers: { Range: SPREADSHEET_PREVIEW_RANGE },
          });
          // S3 returns 206 for partial; 200 if the file is smaller than the range.
          if (response.status !== 200 && response.status !== 206) {
            return {
              error: {
                status: response.status,
                data: "Failed to download preview",
              },
            };
          }
          const text = await response.text();
          return { data: parseSheetPreview(text, delimiter, response.status === 206) };
        } catch (e) {
          return {
            error: {
              status: "FETCH_ERROR" as const,
              error: e instanceof Error ? e.message : "Failed to load preview",
            },
          };
        }
      },
      keepUnusedDataFor: 600,
    }),

    getTextPreview: builder.query<TextPreview, { fileId: string }>({
      queryFn: async ({ fileId }, queryApi) => {
        const urlResult = await queryApi.dispatch(
          accountFilesService.endpoints.getFileRetrieveUrl.initiate({ fileId }),
        );
        if (urlResult.error) return { error: urlResult.error };

        try {
          const response = await fetch(urlResult.data.url, {
            headers: { Range: TEXT_PREVIEW_RANGE },
          });
          if (response.status !== 200 && response.status !== 206) {
            return {
              error: {
                status: response.status,
                data: "Failed to download preview",
              },
            };
          }
          const text = await response.text();
          return { data: parseTextPreview(text, response.status === 206) };
        } catch (e) {
          return {
            error: {
              status: "FETCH_ERROR" as const,
              error: e instanceof Error ? e.message : "Failed to load preview",
            },
          };
        }
      },
      keepUnusedDataFor: 600,
    }),
  }),
});

export const {
  useGetFilesQuery,
  useUploadAccountFileMutation,
  useDeleteAccountFileMutation,
  useGetFileRetrieveUrlQuery,
  useLazyGetFileRetrieveUrlQuery,
  useGetSpreadsheetDataQuery,
  useGetSpreadsheetPreviewQuery,
  useGetTextPreviewQuery,
} = accountFilesService;
